import { ComponentType, SVGProps, useEffect, useMemo } from 'react';

import { useNavigate, useRouterState } from '@tanstack/react-router';
import {
    BookOpen,
    ClipboardCheck,
    FolderOpen,
    FolderTree,
    MessageSquare,
    MessageSquareText,
    Plane,
    Search,
    Ticket,
    Users,
    Wrench,
} from 'lucide-react';

import { useFlag, useProduct } from '@/lib/api';
import { ProductType } from '@/lib/types';

import { useGroundTruthAccess } from '@/features/GroundTruthHub/useGroundTruthAccess';

import { useHybridSorting } from './useHybridSorting';

import { useMaintenanceReviewGuard } from './useMaintenanceReviewGuard';
import { Permission, usePermissions } from './usePermissions';

type MenuItem = {
    title: string;
    icon: ComponentType<SVGProps<SVGSVGElement>>;
    href: string;
    active: boolean;
    testId?: string;
    /** Renders a BETA pill when the sidebar is expanded, an orange dot when collapsed. */
    isBeta?: boolean;
};
// TODO: Refactor these someday; we can probably separate product flag logic from menu construction
export const useMenu = () => {
    const router = useRouterState();
    const navigate = useNavigate();

    // Example usage: Products
    const { data: isTicketAgentEnabled, isLoading: isLoadingTicketAgent } =
        useProduct(ProductType.ticketAgent);
    const { data: isSbAgentEnabled, isLoading: isLoadingSbAgent } = useProduct(
        ProductType.sbAgent,
    );
    const { data: isEndOfLeaseEnabled, isLoading: isLoadingEndOfLease } =
        useProduct(ProductType.endOfLease);
    const {
        data: isMaintenanceReviewEnabled,
        isLoading: isLoadingMaintenanceReview,
    } = useProduct(ProductType.maintenanceReview);
    const { data: isIataBinderEnabled, isLoading: isLoadingIataBinder } =
        useProduct(ProductType.iataBinder);
    const { data: isAsset360Enabled, isLoading: isLoadingAsset360 } =
        useProduct(ProductType.asset360);

    const isLoadingMenu = useMemo(() => {
        return (
            isLoadingTicketAgent ||
            isLoadingSbAgent ||
            isLoadingEndOfLease ||
            isLoadingMaintenanceReview ||
            isLoadingIataBinder ||
            isLoadingAsset360
        );
    }, [
        isLoadingTicketAgent,
        isLoadingSbAgent,
        isLoadingEndOfLease,
        isLoadingMaintenanceReview,
        isLoadingIataBinder,
        isLoadingAsset360,
    ]);
    const { isEnabled: isAssetBinderEnabled } = useHybridSorting();
    const { data: isDocumentSearchEnabled } = useFlag('document_search');
    const { hasPermission } = usePermissions();
    // Gated solely by the single GT access seam (Rules of Hooks: call it
    // unconditionally at the top, alongside the other hooks).
    const { canAccess: canAccessGroundTruth } = useGroundTruthAccess();

    //Redirect guard if maintenance review is disabled
    //Secure by default - only allow access after confirming feature is enabled
    useMaintenanceReviewGuard({
        isMaintenanceReviewEnabled: isMaintenanceReviewEnabled ?? false,
        isLoading: isLoadingMaintenanceReview,
        path: router.location.pathname,
    });

    // Handle automatic redirection from root path to appropriate feature
    useEffect(() => {
        // Only redirect if we're exactly on the root path and have feature data loaded
        if (
            router.location.pathname === '/' &&
            (isTicketAgentEnabled !== undefined ||
                isSbAgentEnabled !== undefined ||
                isEndOfLeaseEnabled !== undefined ||
                isMaintenanceReviewEnabled !== undefined ||
                isIataBinderEnabled !== undefined ||
                isAsset360Enabled !== undefined)
        ) {
            // Priority order: asset360 > ticketAgent > endOfLease > sbAgent > maintenanceReview > iataBinder
            if (isAsset360Enabled) {
                navigate({ to: '/asset_collection' });
                return;
            }
            if (isTicketAgentEnabled) {
                navigate({ to: '/maintenance/agent' });
                return;
            }
            if (isEndOfLeaseEnabled) {
                navigate({ to: '/lease' });
                return;
            }
            if (isSbAgentEnabled) {
                navigate({ to: '/minds-agent' });
                return;
            }
            if (isMaintenanceReviewEnabled) {
                navigate({ to: '/aircrafts' });
                return;
            }
            if (isIataBinderEnabled) {
                navigate({ to: '/iata-binder' });
                return;
            }
        }
    }, [
        router.location.pathname,
        isAsset360Enabled,
        isTicketAgentEnabled,
        isSbAgentEnabled,
        isEndOfLeaseEnabled,
        isMaintenanceReviewEnabled,
        isIataBinderEnabled,
        navigate,
    ]);

    const isActive = (href: string) => {
        return href === '/'
            ? router.location.pathname === '/'
            : router.location.pathname.startsWith(href);
    };

    const createMenuItem = ({
        title,
        icon,
        href,
        testId,
        isBeta,
    }: {
        title: string;
        icon: any;
        href: string;
        testId?: string;
        isLoading?: boolean;
        isBeta?: boolean;
    }) => ({
        title,
        icon,
        href,
        testId,
        isBeta,
        active: isActive(href),
    });
    // We only want to hide Statistics, Conversation & Domain Knowledge if Maintenance Review is the only enabled product
    const isOnlyMaintenanceReviewEnabled = useMemo(() => {
        return (
            isMaintenanceReviewEnabled &&
            !isTicketAgentEnabled &&
            !isSbAgentEnabled &&
            !isEndOfLeaseEnabled &&
            !isAsset360Enabled
        );
    }, [
        isMaintenanceReviewEnabled,
        isTicketAgentEnabled,
        isSbAgentEnabled,
        isEndOfLeaseEnabled,
        isAsset360Enabled,
    ]);

    // Create menu sections
    const toolsMenuItems: MenuItem[] = [];
    const analysisMenuItems: MenuItem[] = [];
    const systemMenuItems: MenuItem[] = [];

    // Tools section - Active features
    if (isAsset360Enabled && hasPermission(Permission.ReadAsset360)) {
        toolsMenuItems.push(
            createMenuItem({
                title: 'Asset 360',
                icon: BookOpen,
                href: '/asset_collection',
                testId: 'MenuItem__toolsAsset360',
            }),
        );
    }

    // Directly under Asset 360: the binder is the same asset's documents, seen the new way.
    if (
        isAsset360Enabled &&
        isAssetBinderEnabled &&
        hasPermission(Permission.ReadAsset360)
    ) {
        toolsMenuItems.push(
            createMenuItem({
                title: 'Asset Binder',
                icon: FolderTree,
                href: '/asset-binder',
                testId: 'MenuItem__toolsAssetBinder',
                isBeta: true,
            }),
        );
    }

    if (isTicketAgentEnabled && hasPermission([Permission.ManageAgents])) {
        toolsMenuItems.push(
            createMenuItem({
                title: 'Ticket Explorer',
                icon: Ticket,
                href: '/maintenance/agent',
                testId: 'MenuItem__toolsTicketAgent',
            }),
        );
    }

    if (isEndOfLeaseEnabled && hasPermission([Permission.ManageEndOfLease])) {
        toolsMenuItems.push(
            createMenuItem({
                title: 'End of Lease Projects',
                icon: Plane,
                href: '/lease',
                testId: 'MenuItem__toolsEndOfLease',
            }),
        );
    }

    if (isMaintenanceReviewEnabled) {
        toolsMenuItems.push(
            createMenuItem({
                title: 'Maintenance Review',
                icon: Wrench,
                href: '/aircrafts',
                testId: 'MenuItem__toolsAircraftReview',
            }),
        );
    }

    if (isIataBinderEnabled && hasPermission([Permission.ManageEndOfLease])) {
        toolsMenuItems.push(
            createMenuItem({
                title: 'IATA Binder',
                icon: FolderTree,
                href: '/iata-binder',
                testId: 'MenuItem__toolsIataBinder',
            }),
        );
    }

    if (isSbAgentEnabled && hasPermission([Permission.ManageAgents])) {
        toolsMenuItems.push(
            createMenuItem({
                title: 'SB Assistant',
                icon: MessageSquareText,
                href: '/minds-agent',
                testId: 'MenuItem__toolsMindsAgent',
            }),
        );
    }

    // Analysis section
    if (hasPermission([Permission.ViewAnalytics])) {
        analysisMenuItems.push(
            createMenuItem({
                title: 'Document Repository',
                icon: FolderOpen,
                href: '/documents',
                testId: 'MenuItem__documentsList',
            }),
        );
        if (isDocumentSearchEnabled) {
            analysisMenuItems.push(
                createMenuItem({
                    title: 'Document Search',
                    icon: Search,
                    href: '/meta-search',
                    testId: 'MenuItem__documentsSearch',
                }),
            );
        }
    }

    // System section
    if (
        hasPermission([Permission.ReadConversations]) &&
        isSbAgentEnabled
    ) {
        systemMenuItems.push(
            createMenuItem({
                title: 'Conversations',
                icon: MessageSquareText,
                href: '/conversations',
                testId: 'MenuItem__systemConversations',
            }),
        );
    }

    if (hasPermission([Permission.ViewTeam])) {
        systemMenuItems.push(
            createMenuItem({
                title: 'Team',
                icon: Users,
                href: '/teams',
                testId: 'MenuItem__systemTeams',
            }),
        );
    }

    if (canAccessGroundTruth) {
        systemMenuItems.push(
            createMenuItem({
                title: 'Ground Truth',
                icon: ClipboardCheck,
                href: '/ground-truth',
                testId: 'MenuItem__systemGroundTruth',
            }),
        );
    }

    systemMenuItems.push(
        createMenuItem({
            title: 'Feedback',
            icon: MessageSquare,
            href: '/feedback',
            testId: 'MenuItem__feedback',
        }),
    );

    // Build menu sections
    const menuSections = [];

    // Add Tools section only if there are enabled tools
    if (toolsMenuItems.length > 0) {
        menuSections.push({
            name: 'Tools',
            menuItems: toolsMenuItems,
        });
    }

    // Add Analysis section
    if (analysisMenuItems.length > 0) {
        menuSections.push({
            name: 'Analysis',
            menuItems: analysisMenuItems,
        });
    }

    // Add System section
    if (systemMenuItems.length > 0) {
        menuSections.push({
            name: 'System',
            menuItems: systemMenuItems,
        });
    }

    return {
        menuSections,
        isLoadingMenu,
    };
};
